import GallerySlideshow from "./GallerySlideshow";
import CountdownTimer from "./CountdownTimer";
import { Button } from "./ui/button";
import { Link } from "react-router-dom";
import { ArrowRight, Ticket, BellRing } from "lucide-react";
import { trackCtaClick, trackVendorApplyClick } from "@/lib/analytics";
import type { Event } from "@/types";
import { format, parseISO } from "date-fns";

interface HeroSectionProps {
  currentEvent?: Event | null;
  countdownDate?: Date;
}

const HeroSection = ({ currentEvent, countdownDate }: HeroSectionProps) => {
  const formattedDate = currentEvent
    ? format(parseISO(currentEvent.date), "MMMM d, yyyy")
    : null;

  return (
    <section className="relative pt-20 overflow-hidden pb-2">

      {/* Hero — unified conversion block */}
      {currentEvent && (
        <div className="container mx-auto px-4 py-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center max-w-5xl mx-auto">
            
            {/* Left: Copy, countdown & CTAs */}
            <div className="text-center md:text-left">
              {/* Authority hook */}
              <p className="text-sm font-semibold uppercase tracking-widest text-primary mb-3">
                Dallas' Premier Sneaker Marketplace
              </p>

              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold mb-3 leading-tight">
                {currentEvent.title}
              </h1>

              <p className="text-muted-foreground text-lg mb-1">
                {formattedDate} · {currentEvent.venue}
              </p>
              {currentEvent.address && (
                <p className="text-muted-foreground text-sm mb-5">
                  {currentEvent.address}
                </p>
              )}

              {/* Authority statement */}
              <p className="text-foreground/80 text-sm md:text-base leading-relaxed mb-6 max-w-md mx-auto md:mx-0">
                Real vendors. Real inventory. Real transactions. The sneaker community starts here.
              </p>

              {/* Countdown */}
              {countdownDate && (
                <div className="max-w-xs mx-auto md:mx-0 mb-6">
                  <CountdownTimer
                    targetDate={countdownDate}
                    eventName={currentEvent.title}
                  />
                </div>
              )}

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center md:justify-start">
                {currentEvent.ticket_url ? (
                  <a
                    href={currentEvent.ticket_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackCtaClick("get_tickets_hero", currentEvent.ticket_url!)}
                  >
                    <Button size="lg" className="gap-2 w-full sm:w-auto">
                      <Ticket className="w-4 h-4" />
                      Get Tickets
                    </Button>
                  </a>
                ) : (
                  <Link
                    to={`/events/${currentEvent.id}`}
                    onClick={() => trackCtaClick("view_details_hero", `/events/${currentEvent.id}`)}
                  >
                    <Button size="lg" className="gap-2 w-full sm:w-auto">
                      View Details
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </Link>
                )}
                <Link
                  to="/apply"
                  onClick={() => {
                    trackCtaClick("become_vendor_hero", "/apply");
                    trackVendorApplyClick("homepage_hero");
                  }}
                >
                  <Button size="lg" variant="outline" className="gap-2 w-full sm:w-auto">
                    Become a Vendor
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right: Event Flyer */}
            {currentEvent.image_url && (
              <div className="flex justify-center md:justify-end">
                <img
                  src={currentEvent.image_url}
                  alt={`${currentEvent.title} flyer`}
                  className="w-full max-w-sm rounded-2xl shadow-lg border border-border"
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Hero — No active event fallback */}
      {!currentEvent && (
        <div className="container mx-auto px-4 py-10">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-sm font-semibold uppercase tracking-widest text-primary mb-3">
              Dallas' Premier Sneaker Marketplace
            </p>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold mb-4 leading-tight">
              Reviving the sneaker community of Dallas-Fort Worth.
            </h1>
            <p className="text-muted-foreground text-lg md:text-xl mb-2">
              Real vendors. Real inventory. Real transactions.
            </p>
            <p className="text-foreground/80 text-base md:text-lg mb-8 max-w-2xl mx-auto">
              Our next Sole to Sole event hasn't been announced yet — but it's coming. Get on the list to be first in line for tickets and vendor spots.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="#notify"
                onClick={() => trackCtaClick("notify_me_hero", "#notify")}
              >
                <Button size="lg" className="gap-2 w-full sm:w-auto">
                  <BellRing className="w-4 h-4" />
                  Notify Me of the Next Event
                </Button>
              </a>
              <Link
                to="/about"
                onClick={() => trackCtaClick("learn_about_hero", "/about")}
              >
                <Button size="lg" variant="outline" className="gap-2 w-full sm:w-auto">
                  Learn About Us
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Gallery Slideshow */}
      <div className="container mx-auto px-4 pt-10 pb-2 text-center">
        <h2 className="font-heading text-2xl md:text-3xl font-bold">Gallery</h2>
        <Link to="/gallery" className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-block mt-1">
          View All →
        </Link>
      </div>
      <GallerySlideshow />
    </section>
  );
};

export default HeroSection;
